import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../../../state/store";
import {
  setDiagnosticsEvents,
  setDiagnosticsErrorsReadingPerfomanceEvents
} from "../../../../state/ducks";
import { selectRum } from "../../../../state/selectors";

import { chartNames, strCode } from "../../../../consts";
import { t } from "../../../../services";
import { dashboardUtil, diagnosticDashboardUtil, rumEventsUtil } from "../../../../utils";
import { useSelectedAppTypes } from "../../../../hooks";

import { DashboardBarChart, DashboardLineChart } from "../../../../components";
import { DashboardTemplate } from "../../../../templates";
import { GridItem } from "../styled";

import DeviceTabsSelector from "../components/DeviceTabsSelector";
import ADStatisticsChart from "../defaultCharts/ADStatisticsChart";
import { useMemo } from "react";

const DiagnosticSection = ({
  getChartFilter = () => {},
  getIsLoading = () => {},
  getIsError = () => {},
  downloadCSV = () => {}
}) => {
  const market = useSelector((store) => store.dashboardFilters.market);

  const diagnostics = useSelector((store) => store.rum.diagnostics);
  const diagnosticsByMarket = useSelector((store) => store.rum.diagnosticsByMarket);
  const diagnosticsErrorsReadingPerfomance = useSelector(
    (store) => store.rum.diagnosticsErrorsReadingPerfomance
  );

  const selectedAppTypes = useSelectedAppTypes();

  const appsWithCompletedDiagnostic = selectedAppTypes?.filter((app) =>
    app.availableDashboards.some((dashboard) => dashboard.name === chartNames.AD_COUNT)
  );

  const onDownloadCSV = () => {
    const title =
      appsWithCompletedDiagnostic?.length > 1
        ? strCode.AD_COUNT_PER_APPLICATION_TITLE
        : market.length > 1
        ? strCode.AD_COUNT_TITLE
        : strCode.AD_COUNT_PER_LOCATION_TITLE;

    const res = diagnosticDashboardUtil.getDiagnosticCountReport(diagnostics?.data);
    downloadCSV(res, t(title));
  };

  const diagnosticPerformanceData = useMemo(() => {
    return diagnostics?.performance?.some((performance) =>
      Object.values(performance.metrics).some((value) => value > 0)
    )
      ? diagnostics?.performance
      : [];
  }, [diagnostics?.performance]);

  return (
    <>
      {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.AD_STATISTICS) && (
        <GridItem gridColumn={"1 / 7"}>
          <ADStatisticsChart
            diagnostics={diagnostics}
            getChartFilter={getChartFilter}
            getIsLoading={getIsLoading}
            getIsError={getIsError}
          />
        </GridItem>
      )}

      {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.AD_COUNT) && (
        <GridItem gridColumn={"1 / 7"}>
          <DashboardTemplate
            id={chartNames.AD_COUNT}
            title={
              appsWithCompletedDiagnostic?.length > 1
                ? strCode.AD_COUNT_PER_APPLICATION_TITLE
                : market.length > 1
                ? strCode.AD_COUNT_TITLE
                : strCode.AD_COUNT_PER_LOCATION_TITLE
            }
            isLoading={getIsLoading(chartNames.AD_COUNT)}
            onDownloadCSV={diagnostics?.data ? onDownloadCSV : null}>
            <DashboardBarChart
              data={diagnosticsByMarket?.statistics}
              isLoading={getIsLoading(chartNames.AD_COUNT)}
              isError={getIsError(chartNames.AD_COUNT)}
              yAxisLabel={strCode.COUNT_LABEL}
              metricsConfig={rumEventsUtil.getMetricsConfig({
                chartName: chartNames.AD_COUNT,
                deviceType: getChartFilter(chartNames.AD_COUNT)
              })}
              isCustomTicks
            />
          </DashboardTemplate>
        </GridItem>
      )}

      {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.DIAGNOSTICS_PERFORMANCE) && (
        <GridItem gridColumn={"1 / 7"}>
          <DashboardTemplate
            id={chartNames.DIAGNOSTICS_PERFORMANCE}
            title={strCode.DIAGNOSTICS_PERFORMANCE_TITLE}
            isLoading={getIsLoading(chartNames.DIAGNOSTICS_PERFORMANCE)}
            Component={DeviceTabsSelector}
            onUpdateChartData={(devices) => {
              const rum = selectRum(getState());
              const data = rum?.diagnostics?.data;

              if (data) {
                const diagnosticsPerformace = diagnosticDashboardUtil.getDiagnosticsPerformance(
                  data,
                  devices
                );

                dispatch(
                  setDiagnosticsEvents({
                    performance: diagnosticsPerformace
                  })
                );
              }
            }}>
            <DashboardLineChart
              data={diagnosticPerformanceData}
              isLoading={getIsLoading(chartNames.DIAGNOSTICS_PERFORMANCE)}
              isError={getIsError(chartNames.DIAGNOSTICS_PERFORMANCE)}
              yAxisLabel={strCode.COUNT_LABEL}
              metricsConfig={rumEventsUtil.getMetricsConfig({
                chartName: chartNames.DIAGNOSTICS_PERFORMANCE,
                deviceType: getChartFilter(chartNames.DIAGNOSTICS_PERFORMANCE),
                coloredMetric: "diagnostics"
              })}
            />
          </DashboardTemplate>
        </GridItem>
      )}

      {dashboardUtil.isChartAvailable(
        selectedAppTypes,
        chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE
      ) && (
        <GridItem gridColumn={"1 / 7"}>
          <DashboardTemplate
            id={chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE}
            title={strCode.DIAGNOSTIC_ERRORS_READING_PEROFOMANCE_TITLE}
            isLoading={getIsLoading(chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE)}
            Component={DeviceTabsSelector}
            onUpdateChartData={(devices) => {
              const rum = selectRum(getState());
              const data = rum?.diagnosticsErrorsReadingPerfomance?.data;

              if (data) {
                const diagnosticsPerformace =
                  diagnosticDashboardUtil.getDiagnosticsErrorsReadingPerfomance(data, devices);

                dispatch(
                  setDiagnosticsErrorsReadingPerfomanceEvents({
                    performance: diagnosticsPerformace
                  })
                );
              }
            }}>
            <DashboardLineChart
              data={diagnosticsErrorsReadingPerfomance?.performance}
              isLoading={getIsLoading(chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE)}
              isError={getIsError(chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE)}
              yAxisLabel={strCode.MAX_AVG_COUNT_LABEL}
              metricsConfig={rumEventsUtil.getMetricsConfig({
                chartName: chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE,
                deviceType: getChartFilter(chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE),
                coloredMetric: "diagnostics"
              })}
            />
          </DashboardTemplate>
        </GridItem>
      )}
    </>
  );
};

DiagnosticSection.propTypes = {
  getChartFilter: PropTypes.func.isRequired,
  getIsLoading: PropTypes.func.isRequired,
  getIsError: PropTypes.func.isRequired,
  downloadCSV: PropTypes.func.isRequired
};

export default DiagnosticSection;
